// History of a script (spec/dashboard.md section 10.8): the commits that touched `extensions/{Name}.js` or `.json`, newest
// first, in a drawer bound to `?history=1`.
import { useEffect, useState } from 'react';
import { ops } from '../../api/operations';
import type { Commit } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { EmptyState } from '../../ui/Banner';
import { AlertCircle, History } from '../../ui/icons';
import { Modal } from '../../ui/Modal';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';

export function HistoryDrawer({ branch, name, onClose }: { branch: string; name: string; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const [items, setItems] = useState<Commit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    client.data(ops.listExtensionHistory, { path: { org, game: game.slug, branch, name } }).then(
      (page) => {
        if (!cancelled) setItems(page.items);
      },
      (e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, org, game.slug, branch, name]);

  return (
    <Modal title={`History of ${name}`} open drawer size="m" onClose={onClose}>
      <div data-testid="script-history">
        {error && (
          <div className="scripts-alert" role="alert">
            <AlertCircle className="scripts-alert-icon" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
        {!error && items === null && (
          <div className="skeleton-rows" role="status" aria-label="Loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        )}
        {items && items.length === 0 && <EmptyState icon={<History />} title="No commits yet." body="Every save of the script is a commit; the first one shows here." />}
        {items && items.length > 0 && (
          <div className="scripts-table-wrap">
          <table className="grid scripts-history">
            <thead>
              <tr>
                <th scope="col">Commit</th>
                <th scope="col">Author</th>
                <th scope="col">Time</th>
                <th scope="col">Message</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.sha} className="grid-row">
                  <td className="mono" title={c.sha}>
                    {c.sha.slice(0, 7)}
                  </td>
                  <td title={c.authorEmail}>{c.authorName}</td>
                  <td>{c.authoredAt ? formatInstantWithZone(c.authoredAt, zone, { offsets }) : '—'}</td>
                  <td className="scripts-history-message">{c.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
